/**
 * Staff Console Types (OptiEdge MDM Phase 1)
 *
 * Cross-tenant shapes consumed by `apps/staff/` (admin console at
 * staff.optisigns.com). Server enforces access via `AdminRoleGuard` on every
 * `staff*` resolver. Customer-facing Device type never exposes vpnIp /
 * vpnPublicKey — those are attached at the staff layer via left-join on
 * mdm-devices.
 */

// --- Admin roles ---

export const ADMIN_ROLES = {
  ROOT: 'root',
  ENGINEER: 'engineer',
  SUPPORT: 'support',
  // Devices Fulfillment only — demo device coupons, the RMA queue, and allocating Amazon
  // coupons (but NOT importing a batch, which is root). Nothing else.
  FULFILLMENT: 'fulfillment',
  // Dunning Review only — nothing else. See AdminRole.BILLING in apps/api.
  BILLING: 'billing',
  // Self-provisioned default. Auto-assigned to every @optisigns.com user on
  // first sign-in. Sees only My Account (Onboarding + Enroll My Laptop).
  READONLY: 'readonly',
} as const;

export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];

/**
 * CONSOLE_ROLES — roles allowed into the cross-tenant console (Customers +
 * Network). `readonly` is the self-provisioned default (My-Account-only). Root
 * additionally sees Admin Users. Used by the sidebar + route guards on the web
 * side and mirrors the backend `@ReadonlyAllowed` default-deny.
 */
export const CONSOLE_ROLES: readonly AdminRole[] = [
  ADMIN_ROLES.ROOT,
  ADMIN_ROLES.ENGINEER,
  ADMIN_ROLES.SUPPORT,
  // FULFILLMENT is deliberately ABSENT. It signs into the same console, but this list means
  // "may reach Customers + Network", and that role may reach neither — its only destination is
  // Devices Fulfillment, which carries its own guard. Adding it here to mean "can log in" would
  // quietly hand it the cross-tenant pages the day someone uses this list to gate one.
  // BILLING is absent for the same reason: its only destination is Dunning Review.
];

/** True when any of the user's roles may access the cross-tenant console. */
export function isConsoleRole(role: AdminRole | undefined | null): boolean {
  return !!role && CONSOLE_ROLES.includes(role);
}

export interface AdminUser {
  _id: string;
  email: string;
  roles: AdminRole[];
  invitedBy: string; // email, or '(seed)' for bootstrapped root users
  invitedAt: string;
  lastLoginAt?: string;
  disabled: boolean;
  ownAccountId?: string;
  createdAt: string;
  updatedAt: string;
}

// Lightweight shape returned by staffMe for sidebar/role gating.
export interface AdminMe {
  email: string;
  roles: AdminRole[];
  disabled: boolean;
  ownAccountId?: string;
  /**
   * Config allowlist, not a role. Rides `staffMe` so the sidebar makes no query.
   *
   * OPTIONAL on purpose: absent must read as DENIED, which makes an older server,
   * a partial cache and a test fixture that predates this field all fail closed by
   * default. Callers compare `=== true` rather than truthiness.
   */
  canDecideDunningReview?: boolean;
  /** The International Requests rollout gate's answer. Optional and `=== true`, as above. */
  intlRequestsVisible?: boolean;
}

// --- Customer search (three independent columns) ---

/** One row per company — deduped by accountId by the backend. */
export interface AccountSearchResult {
  accountId: string;
  companyName?: string;
  ownerEmail?: string;
  ownerName?: string;
}

/** One row per user — no dedupe. companyName belongs to that user's account. */
export interface UserSearchResult {
  userId: string;
  email: string;
  accountId: string;
  companyName?: string;
}

// --- 1-click login / customer resolve ---

export interface UserTeamMembership {
  teamId: string;
  teamName: string;
  isCurrent: boolean;
  /** Team billing — own Stripe subscription + license pool. The fields below only ride with it. */
  isTeamBilling?: boolean | null;
  /** team.payment.isActive — a churned team sub keeps isTeamBilling. */
  teamBillingActive?: boolean | null;
  stripeId?: string | null;
  /** team.limitation.quantity */
  pairedScreens?: number | null;
  /** team.limitation.maxQuantity */
  licensedScreens?: number | null;
}

export interface DomainMatchResult {
  accountId: string;
  companyName?: string;
  plan?: string;
  planFeature?: string;
  isTrial?: boolean;
  isActive?: boolean;
  ownerEmail?: string;
  ownerName?: string;
  ownerUserId?: string;
}

export interface CustomerResolveResult {
  found: boolean;
  inputEmail: string;
  ownerResolvable: boolean;
  accountId?: string;
  companyName?: string;
  plan?: string;
  planFeature?: string;
  isTrial?: boolean;
  isActive?: boolean;
  ownerEmail?: string;
  ownerUserId?: string;
  ownerName?: string;
  inputIsOwner?: boolean;
  /** Role label of the searched user within the company (Owner / Admin / …). */
  inputRole?: string;
  teams?: UserTeamMembership[];
  domainSearched?: string;
  domainMatches?: DomainMatchResult[];
  /**
   * Other accounts whose teams list the searched email — e.g. a person with their own
   * account who is also a member of someone else's team. Empty when there are none.
   */
  otherAccounts?: DomainMatchResult[];
}

export interface CustomerInfo {
  accountId: string;
  payment: string;
  limitation: string;
  feature: string;
  /** Paired screens across the account's regular teams; add to limitation.quantity for the total. */
  teamPairedScreens?: number | null;
}

export interface CustomerInfoAuditEntry {
  _id: string;
  staffEmail: string;
  changedAt: string;
  changes: string; // JSON: { "limitation.maxQuantity": { from: 6, to: 7 } }
  /** Why the account changed — set by machine writes (OptiClaw), absent for panel edits. */
  reason?: string | null;
}

export interface CustomerNote {
  _id: string;
  staffEmail: string;
  createdAt: string;
  text: string;
}

export interface LoginAsCustomerResult {
  appUrl: string;
}

export interface StaffLoginSession {
  _id: string;
  staffEmail: string;
  targetAccountId: string;
  targetOwnerEmail: string;
  loginAt: string;
  active: boolean;
}

/** One row per device — Atlas Search + UUID exact match merged server-side. */
export interface DeviceSearchResult {
  deviceId: string;
  deviceName?: string;
  UUID?: string;
  accountId: string;
  companyName?: string;
  ownerEmail?: string;
  pairingCode?: string;
  /** The device's team name (resolved from teamId); absent for default-team devices. */
  teamName?: string;
}

// Company header + rollups for the customer drill-in page.
export interface StaffCustomer {
  accountId: string;
  companyName: string;
  email?: string;
  plan?: string;
  planFeature?: string;
  totalDevices: number;
  onlineDevices: number;
  staleDevices: number;
  offlineDevices: number;
  totalFleets: number;
  totalTeams: number;
}

// Rich one-call customer read (identity + plan + trial + entitlements + rollups + the
// custops_company ops overlay). Field shape mirrors staff-api's StaffCustomer360 @ObjectType.
export interface StaffCustomer360 {
  accountId: string;
  companyName?: string;
  ownerEmail?: string;
  plan?: string;
  planFeature?: string;
  isTrial: boolean;
  trialEndDate?: string;
  maxQuantity?: number;
  stripeId?: string;
  payment: string;
  limitation: string;
  feature: string;
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  staleDevices: number;
  totalFleets: number;
  totalTeams: number;
  trialExtensionCount: number;
  lastExtendedAt?: string;
}

// --- Staff device shape (with VPN fields joined from mdm-devices) ---

import type { DeviceClassification } from './device-classification';

export interface StaffDevice {
  _id: string;
  accountId?: string;
  teamId?: string;
  UUID?: string;
  deviceName?: string;
  status?: number;
  os?: string;
  osVersion?: string;
  model?: string;
  platform?: string;
  serialNo?: string;
  lastHeartBeat?: string;
  activationDate?: string;
  manufacturer?: string;
  localAppVersion?: string;
  tags?: string[];
  tz?: string;
  usedStorage?: number;
  totalStorage?: number;
  vpnIp?: string;
  vpnRegion?: string;
  vpnPublicKey?: string;
  vpnCreatedAt?: string;
  fleetId?: string | null;
  /** Folder path the device sits under (e.g. "/OnHold Devices"). Null at root. */
  path?: string | null;
  // Current assignment (what the device is playing now).
  currentType?: string | null;
  currentAssignId?: string | null;
  currentAssignName?: string | null;
  // OptiEdge MDM Phase A (spec 04 §3, §4.3) — resolved by the backend
  // DeviceClassificationResolver + StaffDeviceCapabilityResolver, selected
  // by STAFF_CUSTOMER_DEVICES. All four are optional so Phase 1-only callers
  // continue to compile.
  classification?: DeviceClassification | null;
  availableCommands?: string[];
  reportedCapabilities?: string[] | null;
  /**
   * Staff-only: matrix-allowed commands (plan ∩ classification ∩ deviceStateGate),
   * excluding reported-narrowing and fleet deny-list. Used by the 3-column
   * capability diagnostic panel on the staff device detail page. The backend
   * only populates it on the staff `@ResolveField` (see
   * `staff-device-capability.resolver.ts`).
   */
  matrixCapabilities?: string[];
}

// --- Agent devices (personal watchlist) ---

// One row of a staff user's "Agent devices" watchlist. `deviceId` + `addedAt`
// are stored on admin_users.deviceLists; the rest is resolved live by the
// backend (current assignment name joined from assets/playlists/schedules).
// `found` is false when the referenced device no longer exists.
export interface AgentDevice {
  deviceId: string;
  addedAt: string;
  found: boolean;
  // True when the device belongs to an OptiSigns Testing account. Only these rows get drawer
  // actions; other rows (removed, or added before the testing-account gate) are read-only.
  isTestingDevice: boolean;
  // Full live device (same shape as the customer Devices tab) — null when the
  // device no longer exists. Feeds the shared MDM device detail drawer.
  device?: StaffDevice | null;
  // 'asset' | 'playlist' | 'schedule' | 'none'
  currentType?: string | null;
  currentAssignId?: string | null;
  currentAssignName?: string | null;
  // Owner email of the account that owns the assigned content.
  currentAssignOwnerEmail?: string | null;
}

// --- WireGuard / Network ---

// Employee peer row (mdm-devices row with kind='employee') — also used for
// device peers (kind='device'); shape is identical and ownerEmail is simply
// null for devices. deviceName/accountId are populated only by
// staffDevicePeers via a left-join to the `devices` collection.
//
// publicKey is nullable as defense-in-depth: the kind filter (added 2026-04-30)
// prevents cloud-room rows (which have no publicKey) from leaking into these
// queries, but a single mis-tagged row should never wipe an entire response.
export interface EmployeePeer {
  _id: string;
  publicKey?: string;
  name: string;
  ownerEmail?: string;
  vpnIp: string;
  subnet: string;
  region: string;
  createdByRole: string;
  createdAt: string;
  lastHandshakeAt?: string;
  deviceName?: string;
  accountId?: string;
  // 'employee' | 'device' | 'room' — explicit row-type discriminator. Kept
  // wide so adding new kinds doesn't ripple through every consumer.
  kind?: string;
}

// Paginated device peers (mdm-devices row where subnet = 'devices').
export interface DevicePeerConnection {
  items: EmployeePeer[];
  total: number;
}

// Subnet utilization for /staff/network/subnets. `total` is null when the
// subnet is infinite (e.g. /10); UI renders as "N / ∞".
export interface Subnet {
  name: string;
  cidr: string;
  type: 'person' | 'device';
  allowProvisioner: boolean;
  used: number;
  total?: number;
}

/**
 * One account on the dunning ladder's terminal `managerReview` rung.
 *
 * The rung's semantics are "a human now owns this account": no timeout ever
 * advances a review to suspended, and playback keeps running while it waits. So
 * this queue never drains itself — an unactioned row stays `limited` indefinitely,
 * and that is correct rather than a backlog.
 */
/**
 * A Stripe customer excluded from dunning FROM THE BILLING CONSOLE —
 * `staff-app-dunning-exclusion` = `'true'` (stopped) or `'pause'` (+ `untilDate`). Read live
 * from Stripe; distinct from `DunningExclusionRow`, which is staff-app's own Mongo list.
 */
export interface StripeDunningExclusionRow {
  stripeId: string;
  /** 'true' (stopped) or 'pause'. */
  value: string;
  /** A pause's last day, YYYY-MM-DD (US Central). Null when stopped. */
  untilDate?: string | null;
  /** The Stripe customer's own email (where invoices go). */
  stripeEmail?: string | null;
  stripeName?: string | null;
  accountId?: string | null;
  companyName?: string | null;
  /** The portal account owner's email. */
  ownerEmail?: string | null;
  /** payment.isActive / isTrial — the Account status badge (null when no account links). */
  isActive?: boolean | null;
  isTrial?: boolean | null;
  /** Stripe status of the most urgent subscription; null when none (or unreadable). */
  subscriptionStatus?: string | null;
  subscriptionMultiple?: boolean | null;
}

export interface DunningReviewRow {
  accountId: string;
  companyName?: string | null;
  /** 'cc' | 'po' — which ladder ran. Snapshotted at episode start. */
  ladder: string;
  startedAt: string;
  /** Whole days the account has been in dunning. */
  daysInDunning: number;
  screenCount?: number | null;
  amountDue?: string | null;
  invoiceId?: string | null;
  /** Stripe customer id — opens the billing record in one click. */
  stripeId?: string | null;
  /** Non-null means the ladder is frozen — customer replied, or a human paused it. */
  holdReason?: string | null;
  /** Every rung that actually ran, as `branch:step`. */
  history: string[];
  /** When the review rung was reached — how long this has waited on us. */
  reviewSince?: string | null;
  /** payment.isActive / isTrial — the Account status badge. */
  isActive?: boolean | null;
  isTrial?: boolean | null;
  /** Stripe status of the most urgent subscription (review queue); null when none. */
  subscriptionStatus?: string | null;
  subscriptionMultiple?: boolean | null;
  /** Owner + billing email — searched by the page's "email, domain or Stripe ID" box. */
  ownerEmail?: string | null;
  billingEmail?: string | null;
  /** Stripe `staff-app-dunning-exclusion` ('true' / 'pause' / 'false') — review queue only. */
  dunningExclusion?: string | null;
  /** `staff-app-dunning-exclusion-until-date` when paused. */
  dunningExclusionUntil?: string | null;
}
